import React, { useState, type PropsWithChildren, type ReactNode } from "react";
import { Flex, Box, Text } from "@chakra-ui/react";
import { Section, type SectionProps } from "components/section";
import { HubSpotFormModal, type FormProps } from "components/hubspot";
import { AuraCard, type AuraCardProps } from "ui/cards";
import { BookCover } from "ui/book-cover";

type BookFormCardProps = {
  /**
   * Determines the color scheme of the aura card and the book cover
   * @default "green"
   */
  variant?: AuraCardProps["variant"];
  /** The title of the book. */
  title: string;
  /** The text displayed on the button to launch the form modal */
  formLabel: string;
  /** The text displayed on the form submit button in the modal. Use if different from formLabel. */
  formModalLabel?: string;
  /** The ID of the hubspot form to display in the modal. */
  formId: string;
  /** Specify the fields to display in the form modal. */
  formFields: string[];
  /** Success content to display after form submission. */
  successContent: ReactNode;
  /** Content to display above the form. */
  textContent: ReactNode;
  /** Callback to handle form submission. */
  onFormSubmit: FormProps["onSubmit"];
} & Omit<SectionProps, "variant" | "title" | "onSubmit">;

/**
 * Aura card with a book cover, text content,and a hubspot form.
 */
export function BookFormCard({
  variant = "green",
  title,
  formId,
  formFields,
  formLabel,
  formModalLabel,
  textContent,
  successContent,
  onFormSubmit,
  ...sectionProps
}: BookFormCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Section {...sectionProps}>
      <AuraCard variant={variant} px={{ base: "big", md: "biggest", xl: "huge" }} py={{ base: "biggest", xl: "huge" }}>
        <Flex alignItems="center" gap={{ base: "biggest", lg: "huge" }} flexDir={{ base: "column", md: "row" }}>
          <Flex direction="column" maxW={{ base: "100%", md: "300px" }}>
            <BookCover isOpen={isHovered} title={title} colorScheme={variant} />
          </Flex>
          <Flex
            align="start"
            flexDir="column"
            w="100%"
            maxW={{ base: "mediaMaxW.mobile", md: "100%" }}
            mx={{ base: "auto", md: undefined }}
          >
            {textContent}

            <Box
              pos="relative"
              mt="big"
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsHovered(false);
              }}
            >
              <HubSpotFormModal
                hasLaunchButtonChevron
                launchButtonVariant="secondary"
                label={formLabel}
                displayedFields={formFields}
                formId={formId}
                modalHeading={title}
                modalLabel={formModalLabel}
                successContent={successContent}
                onSubmit={onFormSubmit}
              />
            </Box>
          </Flex>
        </Flex>
      </AuraCard>
    </Section>
  );
}

/**
 * Stylized text that can be used within the text content of a book form card.
 */
export function StyledHeading({ children }: PropsWithChildren) {
  return (
    <Text as="h2" textStyle={{ base: "headline2", md: "headline1", lg: "display3" }} mb="small" mt="0">
      {children}
    </Text>
  );
}

export function StyledText({ children }: PropsWithChildren) {
  return (
    <Text textStyle={{ base: "caption1", md: "body3" }} maxW={{ base: "100%", md: "52ch" }}>
      {children}
    </Text>
  );
}
